import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'agenda.appointment.status',
  category: 'agenda',
  title: "Consulter le détail d'un rendez-vous",
  intent:
    'Ouvrir un rendez-vous pour consulter toutes ses informations (statut, client, paiement et coupons liés, prestations, notes et lieu, dates, réponses au formulaire) et accéder aux actions.',
  context: {
    why: "La page détail d'un rendez-vous est le centre de commande de ce rendez-vous — tout ce qu'il faut savoir pour le préparer, l'encaisser et le gérer est réuni au même endroit, sans avoir à jongler entre plusieurs écrans. Cette centralisation vous fait gagner du temps et vous évite les oublis : vous voyez d'un coup d'œil qui vient, pour quelle prestation, à quelle heure, avec quel intervenant, dans quel lieu, avec quel paiement associé, avec quels coupons éventuels, et surtout, avec les réponses au formulaire si vous en aviez configuré un. Cette dernière information est particulièrement précieuse pour les métiers de santé, de thérapie ou de coaching : connaître le motif du rendez-vous, les allergies éventuelles ou le contexte avant de recevoir le client permet une prise en charge beaucoup plus efficace. La page détail sert aussi de point d'entrée pour les actions qui touchent au rendez-vous : reprogrammer (changer le créneau), modifier (éditer les détails) ou annuler. Ces actions sont regroupées dans une cartouche « Actions » qui s'adapte à l'état du rendez-vous — certaines actions disparaissent sur un rendez-vous déjà annulé, par exemple.",
    use_cases: [
      'Vous préparez votre journée en début de matinée et vous consultez les réponses au formulaire de chaque client (motif, allergies, contexte particulier).',
      'Vous vérifiez si un paiement ou un coupon est déjà rattaché au rendez-vous pour savoir ce que vous devez encaisser sur place.',
      "Vous cherchez rapidement le lieu ou l'horaire exact d'un rendez-vous du jour à venir.",
      'Vous voulez consulter la fiche complète du client (historique, coordonnées, notes) : le bouton « Voir la fiche client » vous y emmène directement.',
      'Vous devez agir sur le rendez-vous : reprogrammer, modifier ou annuler — les trois actions sont accessibles depuis cette page.',
      "Vous voulez tracer l'historique de statut d'un rendez-vous, notamment savoir qui l'a annulé et quand.",
    ],
  },
  required_scopes: ['agenda.appointment.read'],
  prerequisites: [
    {
      ref: 'agenda.appointment.create',
      requirement: 'required',
      why: 'Un rendez-vous doit exister pour être consulté en détail.',
    },
  ],
  ui_entry_points: [{ route: '/agenda', element: 'Clic sur un rendez-vous dans la grille → page de détail' }],
  api: {
    primary_operation_id: 'AppointmentAppController_getAppointmentDetail',
    related_operation_ids: [
      'AppointmentAppController_getAppointment',
      'AppointmentAppController_getAppointmentVouchers',
      'ProCustomerAppController_getProCustomerById',
    ],
  },
  steps: [
    '**Ouvrez le rendez-vous** : cliquez sur le RDV dans la grille.',
    '**Lisez le récap en haut** : statut (ex. « Confirmé », « Annulé par le professionnel »), numéro de référence (ex. #39135), format (ex. Individuel), date, horaire, client, prix.',
    "**Consultez les blocs** : **Client** (+ « Voir la fiche client », email, téléphone) · **Paiement & coupons liés** · **Prestations** (intervenant, ressources) · **Notes & lieu** (titre, note, lieu, date de réservation, adresse, date d'annulation) · **Réponses au formulaire** du tunnel si des questions ont été posées.",
    '**Agissez si besoin** : Reprogrammer, Modifier ou Annuler le rendez-vous (voir fiches dédiées).',
  ],
  gotchas: [
    '**3 statuts** : « En attente » → « Confirmé » (badge vert) → « Annulé par le professionnel / par le client » (badge rouge). Un rendez-vous passé peut en plus porter le marquage « Non présenté » (client absent) : il reste confirmé, mais ne compte plus comme une visite.',
    "**Confirmation** : le passage « En attente → Confirmé » est lié à l'option « Confirmer chaque RDV » (validation manuelle) des paramètres de réservation. Sans cette option, la confirmation est automatique.",
    "**Auteur de l'annulation** : le statut précise « par le professionnel » ou « par le client » — utile pour tracer les responsabilités.",
    "**Actions dépendantes du statut** : « Annuler le rendez-vous » n'apparaît que sur un RDV actif. Un RDV déjà annulé ne conserve que Reprogrammer et Modifier.",
    "**Paiement & coupons liés** : onglets dédiés — reflètent ce qui est réellement rattaché au RDV. « Aucun paiement lié » si rien n'est attaché.",
    "**Utiliser un coupon** : depuis l'onglet coupons, scannez le QR code du coupon ou saisissez son code. Le montant ou les prestations du RDV sont pré-remplis, dans la limite du solde du coupon.",
    '**Réponses au formulaire** : visibles en bas de page uniquement si des questions ont été configurées dans les paramètres de réservation (onglet Formulaire).',
    '**Voir la fiche client** : bouton dédié qui ouvre la fiche client complète depuis le RDV, sans devoir chercher.',
    '**Numéro de référence** : identifiant unique du RDV (ex. #39135), utile pour tracer et pour la comm client.',
    '**Format** : reflète le format de la prestation (Individuel, Groupe, Session, Atelier, Renforcé, Libre-service).',
  ],
  related_features: [
    'agenda.appointment.create',
    'agenda.appointment.update',
    'agenda.appointment.cancel',
    'agenda.booking.settings',
  ],
  demo_script: {
    duration_target_s: 30,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'La fiche d’un rendez-vous réunit tout ce qu’il faut pour le préparer et le gérer, au même endroit.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/agenda'),
        caption: "Direction l'Agenda, dans le menu latéral.",
      },
      {
        action: 'wait_for',
        target: TEST_IDS.agenda_calendar,
        caption: 'Votre agenda s’affiche avec vos rendez-vous.',
      },
      {
        action: 'click',
        target: TEST_IDS.agenda_event_card,
        caption: 'On clique sur un rendez-vous pour ouvrir sa fiche complète.',
      },
      {
        action: 'expect_visible',
        target: TEST_IDS.appointment_status_badge,
        caption: 'Statut, client, prestations, paiement, notes… tout est réuni sous vos yeux.',
        capture: true,
      },
      {
        action: 'expect_visible',
        target: TEST_IDS.appointment_view_customer,
        caption: 'Et un accès direct à la fiche client, sans avoir à la chercher.',
        capture: true,
      },
    ],
  },
});

// Verbatim Kaltouma (à conserver) :
// > La page détail d'un RDV montre : statut avec auteur (ex. « Annulé par le
// > professionnel », « Confirmé »), numéro de référence, format,
// > date/horaire/client/prix, la fiche client (+ Voir la fiche client),
// > Paiement & coupons liés, les prestations + intervenant + ressources,
// > Notes & lieu (titre, note, lieu, date de réservation, adresse, date
// > d'annulation), et LES RÉPONSES AUX QUESTIONS du formulaire du tunnel si
// > demandées (tout en bas). Actions : Reprogrammer, Modifier, et Annuler
// > le rendez-vous (sur un RDV actif).
// > [Statuts] Pour le moment : confirmé, en attente et annulé.
